import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import * as _ from 'lodash';
import { TrackeritemService } from '../../services/trackeritem.service';
import { UiService } from '../../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../../core/services/core.translate.service';

/** TrackerExtraField::TYPE_STATUS -- the workflow-driven status field. */
const FIELD_TYPE_STATUS = 7;

/**
 * Quick-add textarea + Field Values panel for bulk-creating children of a Tracker Item
 * (#65237). One line of the textarea = one child's summary; every child inherits the
 * selected tracker's field values shown in the panel (via <ti-field-list>, reused as-is).
 *
 * Reused unmodified as the "Add Sub-Tasks" sub-panel once Iteration 4 adds the segmented
 * Sub-Tasks/Add-Sub-Tasks toggle -- same component instance/inputs, not a copy.
 */
@Component({
  selector: 'ti-breakdown-add',
  template: require('./trackeritem.breakdown-add.component.html')
})
export class BreakdownAddComponent implements OnInit {
  @Input() project: any;
  @Input() tracker: any; // parent's tracker; used as the default selection
  @Input() parentTrackeritem: any;

  @Output() onCreated: EventEmitter<any> = new EventEmitter<any>();

  trackers: any[] = [];
  selectedTracker: any;
  templateTrackeritem: any;
  acls: any = { canUpdate: false };
  quickAddText: string = '';
  isLoaded: boolean = false;
  submitting: boolean = false;

  constructor(
    private trackeritemService: TrackeritemService,
    private uiService: UiService,
    private translateService: GfTranslateService
  ) {}

  ngOnInit(): void {
    this.trackers = _.get(this.project, 'rel.trackers', []);
    this.changeTracker(this.tracker.id);
  }

  changeTracker(trackerId: any): void {
    this.isLoaded = false;
    this.trackeritemService.loadTrackerAndBlankModel(trackerId)
      .then(({ tracker, trackeritem }) => {
        this.selectedTracker = tracker;
        this.templateTrackeritem = this.applyParentDefaults(trackeritem, tracker);
        this.acls = this.trackeritemService.getAcls(this.project, tracker.id, trackeritem);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.isLoaded = true;
      });
  }

  /**
   * Seeds a freshly-built blank model's priority, sprint, and extraFields from the parent item
   * being edited, not the tracker's blank/configured defaults -- a sub-task of a sprint story
   * should land in the same sprint, same milestone, etc. as its parent, not whatever the
   * tracker's admin-configured default happens to be. Only fields still present after the
   * showOnSubmit trim are overridden, and only when the parent actually has a value for that
   * field. Applied once here (not per-child in buildChildPayload) so the Field Values panel
   * -- bound to templateTrackeritem -- actually displays what will be submitted, and so any
   * value the user edits in that panel before hitting submit is respected rather than silently
   * clobbered back to the parent's value at save time.
   *
   * Workflow-driven fields are excluded -- see isWorkflowControlled().
   */
  private applyParentDefaults(trackeritem: any, tracker: any): any {
    if (_.has(this.parentTrackeritem, 'priority')) {
      trackeritem.priority = this.parentTrackeritem.priority;
    }

    const parentSprintData = this.parentTrackeritem?.sprint?.fieldData;
    if (parentSprintData !== undefined) {
      trackeritem.sprint.fieldData = parentSprintData;
    }

    _.forEach(trackeritem.extraFields, (field: any, key: string) => {
      if (this.isWorkflowControlled(tracker, key, field)) {
        return;
      }

      const parentFieldData = _.get(this.parentTrackeritem, ['extraFields', key, 'fieldData']);
      if (parentFieldData !== undefined) {
        field.fieldData = parentFieldData;
      }
    });

    return trackeritem;
  }

  /**
   * A workflow-driven field -- the Status field, plus any select/radio the tracker admin put
   * under workflow control -- may only move between values the tracker's transition graph
   * defines an edge for. The server's baseline for a *new* item is the tracker's default
   * element (TrackerItemMappingService::GetDefaultExtraFieldDatas), not the parent's value
   */
  private isWorkflowControlled(tracker: any, key: string, field: any): boolean {
    const fieldConfig = _.get(tracker, ['fields', key], {});

    if (_.get(fieldConfig, 'useWorkflow', false)) {
      return true;
    }

    return _.get(fieldConfig, 'fieldType', field?.fieldType) === FIELD_TYPE_STATUS;
  }

  private quickAddLines(): string[] {
    return (this.quickAddText || '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }

  /**
   * Builds one child's save payload. priority is an intrinsic field (already top-level on
   * templateTrackeritem, from getBlankModel) -- not duplicated into extraFields. extraFields
   * and sprint are otherwise left exactly as templateTrackeritem carries them (tracker defaults
   * already overridden by the parent's values in applyParentDefaults(), plus any edits made in
   * the Field Values panel) and handed to trackeritemService.save() unmodified -- the same
   * thing trackeritem.add.component.ts does for the standard "Add new item" flow. That flow
   * already proves the shape is fine: empty fieldData ('') is resolved to "no value" by the
   * server (TrackerItemMappingService::MapExtraFieldValuesFromAPI), and full field-element
   * objects are resolved to their id by GetValueFromField. Reducing fieldData to bare ids
   * locally is what caused #65237's multiselect regression -- an array of already-scalar ids
   * takes a different branch in GetValueFromField than an array of objects, and isn't
   * stringified before being handed to explode() in GetElementsForFieldData.
   */
  private buildChildPayload(summary: string): any {
    const child = _.cloneDeep(this.templateTrackeritem);
    child.tracker = { id: this.selectedTracker.id };
    child.summary = summary;
    // No literal "#<id>" here -- that pattern is auto-detected by
    // TrackerItemBackendService::associateTrackerItemRefs() and turned into an "association"
    // record. Parent/child linkage is already fully captured by parentId; a text-mention
    // association would be redundant, and (since the association is recorded against the new
    // item's id before it's assigned one) every child beyond the first collides on the DB's
    // unique (section, ref_id, to_section, to_ref_id) constraint and fails to save.
    child.details = `Created as a child task for parent item ${this.parentTrackeritem.id}: ${this.parentTrackeritem.summary}`;
    child.parentId = this.parentTrackeritem.id;

    return child;
  }

  /**
   * Saves one child at a time, in textarea order, rather than firing every save in parallel --
   * parallel saves can land out of order (each gets its own sort_order/created timestamp from
   * whichever request the server finishes first), which desyncs the created list from the order
   * the user pasted/typed the lines in.
   */
  private saveSequentially(lines: string[]): Promise<boolean[]> {
    const results: boolean[] = [];
    return lines
      .reduce((chain: Promise<any>, summary: string) => {
        return chain
          .then(() => this.trackeritemService.save(this.buildChildPayload(summary), { stopRedirection: true }))
          .then(() => { results.push(true); })
          .catch(() => { results.push(false); });
      }, Promise.resolve())
      .then(() => results);
  }

  submit(): void {
    const lines = this.quickAddLines();
    if (!lines.length || this.submitting) return;

    this.submitting = true;

    this.saveSequentially(lines).then((results: boolean[]) => {
      const successCount = _.filter(results, (ok) => ok).length;
      const total = results.length;
      this.submitting = false;

      if (successCount === total) {
        this.uiService.message.showSuccess(
          this.translateService.translate('{{count}} sub-task(s) created.', { count: successCount }),
          this.translateService.translate('Sub-Tasks Created')
        );
      } else {
        this.uiService.message.showWarning(
          this.translateService.translate('{{success}} of {{total}} sub-task(s) created -- {{failed}} failed.', {
            success: successCount, total: total, failed: total - successCount
          }),
          this.translateService.translate('Some Sub-Tasks Not Created')
        );
      }

      if (successCount > 0) {
        this.quickAddText = '';
        this.onCreated.emit({ successCount, total });
      }
    });
  }
}
